// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AttachableCharge } from '@/billing/api/get-attachable-charges';
import { AttachableChargeAutocomplete } from '@/billing/components/attachable-charge-autocomplete';
import {
  AddLineItemFormData,
  addLineItemFormSchema,
} from '@/billing/schemas/add-line-item-form-schema';
import { FormDatePicker } from '@/components/form-date-picker';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { formatShortDate } from '@/utilities/date-time';
import { formatNumber } from '@/utilities/number';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { Suspense, useEffect } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';

interface AddLineItemFormProps {
  formId: string;
  isDisabled: boolean;
  isOpen: boolean;
  charges: AttachableCharge[];
  onSubmit: (data: AddLineItemFormData) => void;
  // Reports react-hook-form's isDirty up to the parent dialog so it can
  // trigger an unsaved-changes blocker on close.
  onDirtyChange?: (isDirty: boolean) => void;
}

function getDefaultValues(): AddLineItemFormData {
  return {
    charge: null,
    billingEligibleDate: DateTime.now().startOf('day'),
  };
}

function deriveBillingEligibleDate(charge: AttachableCharge | null): DateTime {
  return charge?.billingEligibleDate ?? DateTime.now().startOf('day');
}

export function AddLineItemForm(props: AddLineItemFormProps) {
  const { control, handleSubmit, reset, setValue, formState } =
    useForm<AddLineItemFormData>({
      resolver: zodResolver(addLineItemFormSchema),
      defaultValues: getDefaultValues(),
    });

  useEffect(() => {
    if (props.isOpen) {
      reset(getDefaultValues());
    }
  }, [props.isOpen, reset]);

  const onDirtyChange = props.onDirtyChange;
  useEffect(() => {
    onDirtyChange?.(formState.isDirty);
  }, [formState.isDirty, onDirtyChange]);

  const charge = useWatch({ control, name: 'charge' });

  return (
    <form id={props.formId} onSubmit={handleSubmit(props.onSubmit)} noValidate>
      <Stack spacing={3}>
        <Controller
          name="charge"
          control={control}
          render={({ field, fieldState }) => (
            <AttachableChargeAutocomplete
              size="small"
              label="Charge"
              placeholder="Select a charge"
              value={field.value}
              onChange={(next) => {
                field.onChange(next);
                // Pre-fill billing_eligible_date only while the user has not
                // edited the field — BILLABLE charges carry one already; a
                // user-edited value (dirty) must not be clobbered on charge
                // change.
                if (!formState.dirtyFields.billingEligibleDate) {
                  setValue(
                    'billingEligibleDate',
                    deriveBillingEligibleDate(next)
                  );
                }
              }}
              options={props.charges}
              disabled={props.isDisabled}
              error={fieldState.invalid}
              helperText={fieldState.error?.message}
            />
          )}
        />
        <Suspense fallback={null}>
          <FormDatePicker
            control={control}
            name="billingEligibleDate"
            label="Billing eligible date"
            isDisabled={props.isDisabled}
          />
        </Suspense>
        <ChargePreview charge={charge} />
      </Stack>
    </form>
  );
}

interface ChargePreviewProps {
  charge: AttachableCharge | null;
}

function ChargePreview(props: ChargePreviewProps) {
  if (props.charge === null) return null;
  const { charge } = props;
  const servicePeriod =
    charge.servicePeriodStart && charge.servicePeriodEnd
      ? `${formatShortDate(charge.servicePeriodStart)} – ${formatShortDate(charge.servicePeriodEnd)}`
      : '—';
  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
      }}
    >
      <Typography variant="subtitle2" gutterBottom>
        Charge preview
      </Typography>
      <Divider sx={{ mb: 1.5 }} />
      <Stack spacing={1}>
        <PreviewRow label="Status" value={charge.status} />
        <PreviewRow label="Description" value={charge.label} />
        <PreviewRow label="Service period" value={servicePeriod} />
        <PreviewRow
          label="Item"
          value={`${charge.itemSku} — ${charge.itemName}`}
        />
        <PreviewRow label="Quantity" value={formatNumber(charge.quantity)} />
        <PreviewRow
          label="Unit price"
          value={formatCurrencyValue(charge.unitPrice)}
        />
        <PreviewRow
          labelFontWeight="bold"
          label="Total"
          value={formatCurrencyValue(charge.totalPrice)}
          valueFontWeight="bold"
        />
      </Stack>
    </Box>
  );
}

interface PreviewRowProps {
  label: string;
  value: string;
  valueFontWeight?: 'normal' | 'bold';
  labelFontWeight?: 'normal' | 'bold';
}

function PreviewRow(props: PreviewRowProps) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography
        variant="body2"
        color="text.secondary"
        fontWeight={props.labelFontWeight ?? 'normal'}
      >
        {props.label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={props.valueFontWeight ?? 'normal'}
      >
        {props.value}
      </Typography>
    </Stack>
  );
}
